import React from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, HelpCircle } from 'lucide-react';
import { mockFAQs } from '../mockData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQ = () => {
  const additionalFAQs = [
    {
      question: "What technology do I need?",
      answer: "You need a smartphone, tablet, or computer with a camera, microphone, and internet connection. Most modern devices work great. No app download is required—everything works in your web browser."
    },
    {
      question: "Can I get a prescription?",
      answer: "Yes. Our providers can prescribe medications when medically appropriate and permitted by law. Prescriptions are sent electronically to your preferred pharmacy. Some controlled substances may not be available via telehealth."
    },
    {
      question: "What if I need lab work or imaging?",
      answer: "If your provider determines you need lab work or imaging, they'll provide orders you can take to a local facility. We'll follow up on results and adjust your treatment plan as needed."
    },
    {
      question: "Can I see the same provider again?",
      answer: "While we can't guarantee the same provider each time, you can request to see a previous provider for follow-up visits when they're available."
    },
    {
      question: "What states do you serve?",
      answer: "We serve patients in all 50 states. Our providers are licensed in multiple states to ensure you can receive care wherever you're located."
    },
    {
      question: "Is my visit confidential?",
      answer: "Absolutely. All visits are completely confidential. We use encrypted video connections and follow strict privacy protocols. Your health information is protected with safeguards designed to meet healthcare privacy standards."
    }
  ];

  const allFAQs = [...mockFAQs, ...additionalFAQs];

  const faqCategories = [
    {
      title: "Getting Started",
      faqs: allFAQs.slice(0, 4)
    },
    {
      title: "Coverage & Payment",
      faqs: allFAQs.slice(4, 8)
    },
    {
      title: "Technology & Privacy",
      faqs: allFAQs.slice(8, 12)
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-[#0F4C81] to-[#1FB6B2] mb-6">
            <HelpCircle className="text-white" size={40} />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-[#12324A] mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-[#5F6C7B] max-w-3xl mx-auto">
            Find answers to common questions about TouchScreenMD. Can't find what you're looking for? Contact our support team.
          </p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {faqCategories.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                <h2 className="text-3xl font-bold text-[#12324A] mb-8 pb-4 border-b-2 border-[#1FB6B2]">
                  {category.title}
                </h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {category.faqs.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`item-${categoryIndex}-${index}`}
                      className="border-2 border-gray-200 rounded-xl px-6 bg-white hover:border-[#1FB6B2] transition-colors"
                    >
                      <AccordionTrigger className="text-left font-semibold text-[#12324A] hover:text-[#0F4C81] py-6">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#5F6C7B] pb-6 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions Section */}
      <section className="py-20 bg-[#F4F6F8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-[#12324A] mb-6">
            Still Have Questions?
          </h2>
          <p className="text-xl text-[#5F6C7B] mb-8">
            Our support team is here to help. Reach out anytime.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#0F4C81] hover:bg-[#12324A] text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              Contact Support
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-[#0F4C81] text-[#0F4C81] hover:bg-[#0F4C81] hover:text-white px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200"
            >
              Live Chat
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0F4C81] to-[#12324A] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl mb-8 text-gray-200">
            Connect with a licensed provider and experience quality healthcare today.
          </p>
          <Button
            size="lg"
            className="bg-white text-[#0F4C81] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-xl transition-all duration-200 shadow-lg"
          >
            Start a Visit Now
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
